import React from "react";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { Check, Circle, LoaderCircle, X } from "lucide-react";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { LifecycleScriptStatus } from "components/Environment/shared/BuildTypes";
import {
  getEnvironmentStatus,
  getLatestTask,
  normalizeEnvironmentStatus,
  TaskName,
  TaskStatus,
} from "components/Environment/utils/environtmentUtils";
import type { Environment } from "components/Environment/utils/types";
import type {
  SetupStep,
  SetupStepState,
} from "components/Environment/shared/EnvironmentSetupLights";
import { WorkspaceStatus } from "models/Workspace.model";
import { cn } from "lib/utils";
import {
  RICH_TOOLTIP_CONTENT_CLASS_NAME,
  TOOLTIP_ARROW_CLASS_NAME,
} from "components/Environment/shared/DisabledActionTooltip";

export const hasFailedStartupScript = (environment: Environment): boolean =>
  environment.lifecycle_scripts?.some(
    (script) => script.last_run?.status === LifecycleScriptStatus.FAILED,
  ) === true;

export const hasRunningStartupScript = (environment: Environment): boolean =>
  environment.lifecycle_scripts?.some(
    (script) => script.last_run?.status === LifecycleScriptStatus.RUNNING,
  ) === true;

const completedEnvironmentStatuses = new Set([
  EnvironmentStatus.RUNNING,
  EnvironmentStatus.STARTING,
  EnvironmentStatus.STARTING_FAILED,
  EnvironmentStatus.STOPPING,
  EnvironmentStatus.STOPPING_FAILED,
  EnvironmentStatus.STOPPED,
  EnvironmentStatus.TERMINATING,
  EnvironmentStatus.TERMINATING_FAILED,
  EnvironmentStatus.TERMINATED,
  EnvironmentStatus.INSTANCE_LOST,
]);

const taskState = (
  environment: Environment,
  taskName: string,
): SetupStepState | null => {
  const task = getLatestTask(taskName, environment.tasks ?? []);
  switch (task?.status) {
    case TaskStatus.SUCCEEDED:
      return "complete";
    case TaskStatus.RUNNING:
      return "active";
    case TaskStatus.WAITING:
      return "upcoming";
    case TaskStatus.FAILED:
    case TaskStatus.CANCELED:
      return "failed";
    default:
      return null;
  }
};

export const hasFailedBuild = (environment: Environment): boolean =>
  normalizeEnvironmentStatus(environment.status) ===
  EnvironmentStatus.BUILDING_FAILED;

const provisioningState = (environment: Environment): SetupStepState => {
  const status = normalizeEnvironmentStatus(environment.status);
  if (
    status === EnvironmentStatus.PROVISIONING_FAILED ||
    status === EnvironmentStatus.PREPARING_FAILED
  ) {
    return "failed";
  }
  if (
    status === EnvironmentStatus.PROVISIONING ||
    status === EnvironmentStatus.PREPARING
  ) {
    return "active";
  }
  if (status === EnvironmentStatus.PENDING) return "upcoming";
  if (
    status === EnvironmentStatus.BUILDING ||
    status === EnvironmentStatus.BUILDING_FAILED ||
    (status !== null && completedEnvironmentStatuses.has(status))
  ) {
    return "complete";
  }

  return (
    taskState(environment, TaskName.ENVIRONMENT_PROVISION) ??
    (getEnvironmentStatus(environment) === WorkspaceStatus.Deploying
      ? "active"
      : "complete")
  );
};

const buildingState = (environment: Environment): SetupStepState => {
  const status = normalizeEnvironmentStatus(environment.status);
  if (status === EnvironmentStatus.BUILDING_FAILED) return "failed";
  if (status === EnvironmentStatus.BUILDING) return "active";
  if (status !== null && completedEnvironmentStatuses.has(status)) {
    return "complete";
  }
  if (
    status === EnvironmentStatus.PENDING ||
    status === EnvironmentStatus.PREPARING ||
    status === EnvironmentStatus.PREPARING_FAILED ||
    status === EnvironmentStatus.PROVISIONING ||
    status === EnvironmentStatus.PROVISIONING_FAILED
  ) {
    return "upcoming";
  }

  const buildTaskState = taskState(environment, TaskName.ENVIRONMENT_BUILD);
  if (buildTaskState) return buildTaskState;

  const workspaceStatus = getEnvironmentStatus(environment);
  if (
    workspaceStatus === WorkspaceStatus.Running ||
    workspaceStatus === WorkspaceStatus.Starting ||
    workspaceStatus === WorkspaceStatus.Stopping ||
    workspaceStatus === WorkspaceStatus.Stopped ||
    workspaceStatus === WorkspaceStatus.Deleting
  ) {
    return "complete";
  }
  return provisioningState(environment) === "complete" ? "active" : "upcoming";
};

const startupScriptState = (
  environment: Environment,
): SetupStepState | null => {
  const lifecycleScript = environment.lifecycle_scripts?.[0];
  if (!lifecycleScript) return null;

  switch (lifecycleScript.last_run?.status) {
    case LifecycleScriptStatus.COMPLETED:
      return "complete";
    case LifecycleScriptStatus.RUNNING:
      return "active";
    case LifecycleScriptStatus.PENDING:
      return "upcoming";
    case LifecycleScriptStatus.FAILED:
      return "failed";
    default:
      return "upcoming";
  }
};

export const getEnvironmentSetupSteps = (
  environment: Environment,
): SetupStep[] => {
  const provision = provisioningState(environment);
  const build = buildingState(environment);
  const startupScript = startupScriptState(environment);
  const setupFailed =
    provision === "failed" || build === "failed" || startupScript === "failed";
  const setupInProgress =
    provision === "active" || build === "active" || startupScript === "active";
  const workspaceStatus = getEnvironmentStatus(environment);
  const hasReachedReady =
    workspaceStatus === WorkspaceStatus.Running ||
    workspaceStatus === WorkspaceStatus.Starting ||
    workspaceStatus === WorkspaceStatus.Stopping ||
    workspaceStatus === WorkspaceStatus.Stopped ||
    workspaceStatus === WorkspaceStatus.Deleting;

  return [
    { label: "Provisioning", state: provision },
    { label: "Building", state: build },
    ...(startupScript
      ? [{ label: "Running startup script", state: startupScript }]
      : []),
    {
      label: "Ready",
      state:
        hasReachedReady && !setupFailed && !setupInProgress
          ? "complete"
          : "upcoming",
    },
  ];
};

const stateLabel: Record<SetupStepState, string> = {
  complete: "Complete",
  active: "In progress",
  failed: "Failed",
  upcoming: "Waiting",
};

const isStartupStep = (label: string): boolean =>
  label.toLowerCase().includes("startup");

const StepIcon = ({
  label,
  state,
}: {
  label: string;
  state: SetupStepState;
}) => {
  if (state === "complete") {
    return (
      <span className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-highlight text-black">
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span
        className={cn(
          "relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1",
          isStartupStep(label)
            ? "bg-highlight/15 text-highlight ring-highlight/40"
            : "bg-pendingBlue/15 text-pendingBlue ring-pendingBlue/40",
        )}
      >
        <LoaderCircle className="h-3 w-3 animate-spin" aria-hidden="true" />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-400 ring-1 ring-red-500/40">
        <X className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  return (
    <span className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center text-zinc-600">
      <Circle className="h-3 w-3" aria-hidden="true" />
    </span>
  );
};

interface EnvironmentStatusStepperProps {
  environment: Environment;
  statusLabel: string;
  children: React.ReactElement;
  triggerClassName?: string;
}

const EnvironmentStatusStepper: React.FC<EnvironmentStatusStepperProps> = ({
  environment,
  statusLabel,
  children,
  triggerClassName,
}) => {
  const steps = getEnvironmentSetupSteps(environment);

  return (
    <TooltipPrimitives.Provider delayDuration={300}>
      <TooltipPrimitives.Root>
        <TooltipPrimitives.Trigger asChild>
          <button
            type="button"
            className={cn(
              "cursor-help rounded-sm border-0 bg-transparent p-0 text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight",
              triggerClassName,
            )}
            aria-label={`${statusLabel}. Show environment setup progress`}
          >
            {children}
          </button>
        </TooltipPrimitives.Trigger>
        <TooltipPrimitives.Portal>
          <TooltipPrimitives.Content
            side="bottom"
            align="start"
            sideOffset={8}
            className={RICH_TOOLTIP_CONTENT_CLASS_NAME}
          >
            <p className="mb-3 text-sm font-semibold text-zinc-200">
              Environment setup
            </p>
            <ol aria-label="Environment setup progress">
              {steps.map((step, index) => (
                <li
                  key={step.label}
                  className="relative flex min-h-9 items-start gap-3 last:min-h-5"
                >
                  {index < steps.length - 1 && (
                    <span
                      className={cn(
                        "absolute bottom-0 left-[9px] top-5 z-0 w-px",
                        step.state === "complete"
                          ? "bg-[color:var(--color-highlight)] opacity-60"
                          : "bg-zinc-500/70",
                      )}
                      aria-hidden="true"
                    />
                  )}
                  <StepIcon label={step.label} state={step.state} />
                  <span className="flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5">
                    <span
                      className={cn(
                        "text-sm",
                        step.state === "upcoming"
                          ? "text-zinc-400"
                          : "text-zinc-200",
                      )}
                    >
                      {step.label}
                    </span>
                    <span
                      className={cn(
                        "shrink-0 text-sm",
                        step.state === "complete" && "text-highlight",
                        step.state === "active" &&
                          (isStartupStep(step.label)
                            ? "text-highlight"
                            : "text-pendingBlue"),
                        step.state === "failed" && "text-red-400",
                        step.state === "upcoming" && "text-zinc-400",
                      )}
                    >
                      {stateLabel[step.state]}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
            <TooltipPrimitives.Arrow className={TOOLTIP_ARROW_CLASS_NAME} />
          </TooltipPrimitives.Content>
        </TooltipPrimitives.Portal>
      </TooltipPrimitives.Root>
    </TooltipPrimitives.Provider>
  );
};

export default EnvironmentStatusStepper;
